/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  TypographyProps,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { ButtonPrimary } from "../components-atoms";
import { ConfirmModal } from "../components-molecules/ConfirmModal";
import { Countdown } from "../components-molecules/Countdown";
import { InfoSectionContainer } from "../components-molecules/InfoSectionContainer";
import { CollectPricing } from "../redux/reducers/orderDetails";
import { colours, theme } from "../constants/theme";
import CollectionOrderCourierSelection from "./CollectionOrderCourierSelection";
import { IAvailableCourierItemProps } from "../components-molecules/AvailableCourierItem";
import { useAppWebSocket } from "../hooks/useAppWebSocket";
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { ErrorModal } from "../components-molecules";
import { ReadyState } from "react-use-websocket";

const FIFTEEN_MINS = 15 * 60;

export enum CollectionOrderTotalSource {
  Form = 'Form',
  Detail = 'Detail'
}

const useStyles = makeStyles(() => ({
  infoButtonContainer: {
    marginTop: -6
  },
  infoButton: {
    width: 40,
    height: 40
  }
}));

export interface INewCollectionOrderTotalProps {
  lastUpdate: Date;
  orderId?: string;
  getPriceLoading: boolean;
  onGetPrice: () => void;
  onExpire: () => void;
  distanceBasedPrice: {
    MinPrice: CollectPricing;
    MaxPrice: CollectPricing;
  };
  onCourierChange: (courier: IAvailableCourierItemProps) => void;
  hasError: boolean;
  selectedCouriers: string[];
  onSubmit: (id: string) => void;
  source?: CollectionOrderTotalSource;
  maxPriceQuoteId?: string;
  setMaxPriceQuoteId: (id: string | null) => void;
}

export function NewCollectionOrderTotal(props: any) {
  const {
    getPriceLoading,
    placeOrderLoading,
    lastUpdate,
    onGetPrice,
    orderId,
    onExpire,
    onCourierChange,
    hasError,
    onSubmit,
    source = CollectionOrderTotalSource.Form,
    maxPriceQuoteId,
    setMaxPriceQuoteId
  } = props;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [noCouriers, setNoCouriers] = useState(false);
  const [countdownStarted, setCountdownStarted] = useState(false);
  const [availableCouriers, setAvailableCouriers] = useState<any>();
  const [modalOpen, setModalOpen] = useState(false);
  const [estimatedPriceVisible, setEstimatedPriceVisible] = useState(false);
  const isAdminPortal = source === CollectionOrderTotalSource.Detail;
  const [buttonDisabled, setButtonDisabled] = useState(isAdminPortal);

  const mobileView = useMediaQuery("(max-width: 899px)", { noSsr: true });
  const hasQuotes = !loading && availableCouriers?.ExpiryDate?.length > 0;
  const { lastJsonMessage, readyState } = useAppWebSocket<any>(
    '/ws/collect-quotes',
    {
      queryParams: { pcid: orderId }
    },
    Boolean(orderId && !hasQuotes)
  );

  const COUNTDOWN_TIME = useMemo(() => {
    const expiryDate = lastJsonMessage?.AvailableCouriersResponseModel?.ExpiryDate;
    if (expiryDate) {
      const endDate = moment.utc(expiryDate);
      const diff = endDate.diff(moment().utc(), 'seconds');
      return diff;
    }
    return FIFTEEN_MINS;
  }, [lastJsonMessage]);

  useEffect(() => {
    const isAdminPortal = source === CollectionOrderTotalSource.Detail;
    const isConnected = readyState === ReadyState.OPEN;
    if (isAdminPortal && isConnected && lastJsonMessage && buttonDisabled) {
      setButtonDisabled(false);
    }
  }, [readyState, source, lastJsonMessage]);

  useEffect(() => {
    if (
      typeof lastJsonMessage?.AvailableCouriersLoadingModel === "object"
      && lastJsonMessage?.AvailableCouriersLoadingModel?.Loading
    ) {
      setNoCouriers(false);
      setLoading(true);
      setButtonDisabled(true);
    }
  }, [lastJsonMessage])

  useEffect(() => {
    if (typeof lastJsonMessage?.CouriersNotFoundResponseModel === "object"
      && lastJsonMessage?.CouriersNotFoundResponseModel?.Error
    ) {
      setNoCouriers(true);
      setLoading(false);
      setButtonDisabled(false);
      setCountdownStarted(false);
      setAvailableCouriers(null);
    }
  }, [lastJsonMessage]);

  const countdownProps: TypographyProps = {
    display: "inline",
    variant: "caption",
    style: {
      marginTop: mobileView ? 0 : 24,
      marginBottom: mobileView ? 0 : -12,
      color: theme.palette.error.main,
      fontWeight: 400,
      letterSpacing: 0.15,
    },
  };

  useEffect(() => {
    if (
      lastJsonMessage?.AvailableCouriersResponseModel?.AvailableCouriers?.length > 0 ||
      lastJsonMessage?.AvailableCouriersResponseModel?.Premium?.Id
    ) {
      setNoCouriers(false);
      setAvailableCouriers(lastJsonMessage?.AvailableCouriersResponseModel);
      setCountdownStarted(true);
      setLoading(false);
      setButtonDisabled(false);
    }
  }, [lastJsonMessage]);

  useEffect(() => {
    if (lastUpdate) {
      setCountdownStarted(false);
      setLoading(false);
      setButtonDisabled(false);
    }
  }, [lastUpdate]);

  useEffect(() => {
    if (getPriceLoading) {
      setLoading(true);
    }
  }, [getPriceLoading]);

  const handleGetPrice = () => {
    if (!hasError) {
      setNoCouriers(false);
      setAvailableCouriers(null);
      setCountdownStarted(false);
      setLoading(true);
      setButtonDisabled(true);
    }
    onGetPrice();
  };

  const onCountdownEnd = () => {
    setCountdownStarted(false);
    setButtonDisabled(false);
    setModalOpen(true);
  };

  const renderInfoPopup = () => {
    return (
      <ErrorModal
        visible={estimatedPriceVisible}
        title="Estimated price"
        buttonTitle="Dismiss"
        errorBody={"Each pick up and drop off has a 10 minute grace period. After 10 minutes, incremental wait time will be charged at 50p per minute."}
        onClose={() => {
          setEstimatedPriceVisible(false)
        }}
      />
    )
  }

  const renderInfoButton = () => {
    return (
      <Box className={classes.infoButtonContainer}>
        <IconButton className={classes.infoButton} onClick={() => setEstimatedPriceVisible(true)}>
          <InfoOutlinedIcon htmlColor={colours.highEmphasis54} fontSize="small" />
        </IconButton>
      </Box>
    )
  }

  const headerRight = () => {
    if (!countdownStarted) {
      return (
        <ButtonPrimary
          data-testid="get-price"
          onClick={handleGetPrice}
          disabled={getPriceLoading || hasError || buttonDisabled}
        >
          Get Price
        </ButtonPrimary>
      );
    }
    return (
      <Typography {...countdownProps}>
        Price valid for{" "}
        <Countdown
          time={COUNTDOWN_TIME}
          onEnd={onCountdownEnd}
          {...countdownProps}
        />
      </Typography>
    );
  };

  const renderInfo = () => {
    return (
      <>
        {source === CollectionOrderTotalSource.Detail ?
          <Typography variant="body2">
            Click Get Price button to get the price of the order.
          </Typography>
          :
          <Typography variant="body2">
            Fill out the form above and click Get Price button to get the price of
            the order.
          </Typography>}
        <Typography variant="body2">
          The price for the order is valid for 15 min.
        </Typography>
      </>
    );
  };

  const renderLoading = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress style={{ marginBottom: 16 }} />
        <Typography>Getting the best price for you.</Typography>
        <Typography>This process can take up to two minutes.</Typography>
      </Box>
    );
  };

  const renderNoCouriers = () => {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
        <Typography>No delivery options available for this order.</Typography>
        <Typography>Please try changing the configuration and get a new price.</Typography>
      </Box>
    );
  };

  const expiredModal = () => {
    return (
      <ConfirmModal
        visible={modalOpen}
        title="Your quote has expired"
        buttonTitle="Get Price"
        errorBody="The price quote is only valid for a period of 15 min. Press the Get Price button again to get a new quote and proceed with the order."
        onSubmit={() => {
          setLoading(true);
          setModalOpen(false);
        }}
        onClose={() => {
          onExpire?.();
          setModalOpen(false);
        }}
      />
    );
  };

  return (
    <InfoSectionContainer
      title="Estimated price"
      helperText="The quote includes our standard wait time agreement, which includes loading/unloading. If there are excessive wait times additional charges will apply."
      headerLeft={() => renderInfoButton()}
      headerRight={() => headerRight()}
      headerStyle={{ maxWidth: "80%" }}
      sx={{ mt: 3 }}
    >
      <Box mt={2}>
        {noCouriers ? (
          renderNoCouriers()
        ) : loading ? (
          renderLoading()
        ) : countdownStarted ? (
          <CollectionOrderCourierSelection
            placeOrderLoading={placeOrderLoading}
            couriers={availableCouriers}
            onChange={(e) => onCourierChange(e)}
            hasError={hasError}
            onSubmit={onSubmit}
            maxPriceQuoteId={maxPriceQuoteId}
            setMaxPriceQuoteId={setMaxPriceQuoteId}
          />
        ) : (
          renderInfo()
        )}
      </Box>

      {renderInfoPopup()}
      {expiredModal()}
    </InfoSectionContainer>
  );
}
